import { DriverNo } from "generated/sales";
import { useEffect, useMemo, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { IExpensesList } from "types/Logistic";

export type TSummary = {
  expenses_post_discount_amount: number;
  expenses_vat_exempted_amount: number;
  expenses_vat_0_percent_amount: number;
  expenses_vat_7_percent_amount: number;
  expenses_vat_amount: number;
  expenses_net_amount: number;
  expenses_withholding_tax_amount: number;
  expenses_total_amount: number;
};

const defaultSummary: TSummary = {
  expenses_post_discount_amount: 0,
  expenses_vat_exempted_amount: 0,
  expenses_vat_0_percent_amount: 0,
  expenses_vat_7_percent_amount: 0,
  expenses_vat_amount: 0,
  expenses_net_amount: 0,
  expenses_withholding_tax_amount: 0,
  expenses_total_amount: 0,
};

export const expensesSummary = (
  expensesList: IExpensesList[] = []
): TSummary => {
  let expenses_post_discount_amount = 0;
  let expenses_vat_exempted_amount = 0;
  let expenses_vat_0_percent_amount = 0;
  let expenses_vat_7_percent_amount = 0;
  let expenses_vat_amount = 0;
  let expenses_net_amount = 0;
  let expenses_withholding_tax_amount = 0;
  let expenses_total_amount = 0;

  const postDiscountAmount = expensesList.reduce(
    (acc, item) => acc + (item.pre_vat_amount || 0),
    0
  );
  expenses_post_discount_amount = postDiscountAmount;

  const vatExemptedAmount = expensesList
    .filter((item) => item?.vat_percentage === "ไม่มี")
    .reduce((acc, item) => acc + (item.pre_vat_amount || 0), 0);
  expenses_vat_exempted_amount = vatExemptedAmount;

  const vat0percentAmount = expensesList
    .filter((item) => item?.vat_percentage === "0")
    .reduce((acc, item) => acc + (item.pre_vat_amount || 0), 0);
  expenses_vat_0_percent_amount = vat0percentAmount;

  const vat7percentAmount = expensesList
    .filter((item) => item?.vat_percentage === "7")
    .reduce((acc, item) => acc + (item.pre_vat_amount || 0), 0);
  expenses_vat_7_percent_amount = vat7percentAmount;

  expenses_vat_amount = vat7percentAmount * 0.07;

  expenses_net_amount =
    expenses_vat_exempted_amount +
    expenses_vat_0_percent_amount +
    expenses_vat_7_percent_amount +
    expenses_vat_amount;

  const withholdingTaxAmount =
    expensesList
      .filter(
        (item) =>
          !["ยังไม่ระบุ", "ไม่มี", "", undefined, null].includes(
            item?.withholding_tax_type as any
          )
      )
      .reduce((acc, item) => {
        const base =
          item.pre_vat_amount && item.pre_vat_amount < 0
            ? 0
            : item.pre_vat_amount || 0;
        const pct = parseFloat(item?.withholding_tax_type || "0");
        return acc + (base * pct) / 100;
      }, 0) || 0;

  expenses_withholding_tax_amount =
    expenses_post_discount_amount <= 0 ? 0 : withholdingTaxAmount;

  expenses_total_amount =
    expenses_net_amount - expenses_withholding_tax_amount;

  return {
    expenses_post_discount_amount,
    expenses_vat_exempted_amount,
    expenses_vat_0_percent_amount,
    expenses_vat_7_percent_amount,
    expenses_vat_amount,
    expenses_net_amount,
    expenses_withholding_tax_amount,
    expenses_total_amount,
  };
};

export const useExpensesSummary = () => {
  const { control, setValue, getValues } = useFormContext();

  const watchedExpensesList = useWatch({
    control,
    name: "expenses_list",
  });

  const expensesList: IExpensesList[] = useMemo(
    () => (watchedExpensesList ?? []) as IExpensesList[],
    [watchedExpensesList]
  );

  const [summary, setSummary] = useState<TSummary>(defaultSummary);

  // 86d4b1mkf — the driver-2 strip-on-mount / restore effects that used to
  // sit here are gone. This hook is instantiated three times per document
  // (one `ExpnesesSummary` per driver block plus the total), so that was three
  // more writers of `expenses_list` racing the ones in `ExpensesList`.
  // Keeping rows consistent with the drivers is `ExpensesList`'s job alone
  // now; this hook only reads.

  useEffect(() => {
    const newSummary = expensesSummary(expensesList);
    setSummary(newSummary);
  }, [expensesList]);

  useEffect(() => {
    const prev = getValues();

    const map: Array<[keyof TSummary, number]> = [
      ["expenses_post_discount_amount", summary.expenses_post_discount_amount],
      ["expenses_vat_exempted_amount", summary.expenses_vat_exempted_amount],
      ["expenses_vat_0_percent_amount", summary.expenses_vat_0_percent_amount],
      ["expenses_vat_7_percent_amount", summary.expenses_vat_7_percent_amount],
      ["expenses_vat_amount", summary.expenses_vat_amount],
      ["expenses_net_amount", summary.expenses_net_amount],
      ["expenses_withholding_tax_amount", summary.expenses_withholding_tax_amount],
      ["expenses_total_amount", summary.expenses_total_amount],
    ];

    map.forEach(([k, v]) => {
      const current = (prev as any)?.[k];
      if (Object.is(current, v)) return;
      setValue(k as any, v, {
        shouldDirty: false,
        shouldTouch: false,
        shouldValidate: false,
      });
    });
  }, [setValue, summary, getValues]);

  useEffect(() => {
    if (!expensesList || expensesList.length === 0) return;

    const primaryList = expensesList.filter(
      (item) => (item.driver_no ?? DriverNo.Primary) === DriverNo.Primary
    );
    const secondaryList = expensesList.filter(
      (item) => item.driver_no === DriverNo.Secondary
    );

    const primarySummary = expensesSummary(primaryList);
    const secondarySummary = expensesSummary(secondaryList);

    expensesList.forEach((row, index) => {
      const driverNo = (row.driver_no ?? DriverNo.Primary) as DriverNo;
      const s =
        driverNo === DriverNo.Primary ? primarySummary : secondarySummary;

      const updates: any = {};

      if (row.net_amount !== s.expenses_net_amount)
        updates.net_amount = s.expenses_net_amount;

      if (row.withholding_tax_amount !== s.expenses_withholding_tax_amount)
        updates.withholding_tax_amount = s.expenses_withholding_tax_amount;

      if (row.total_amount !== s.expenses_total_amount)
        updates.total_amount = s.expenses_total_amount;

      if (Object.keys(updates).length > 0) {
        Object.entries(updates).forEach(([k, v]) => {
          setValue(`expenses_list.${index}.${k}` as any, v, {
            shouldDirty: false,
            shouldTouch: false,
            shouldValidate: false,
          });
        });
      }
    });
  }, [expensesList, setValue]);

  return summary;
};

export const useDriverExpensesSummary = (
  source: "driver1" | "driver2"
): TSummary => {
  const { control } = useFormContext();

  const watchedExpensesList = useWatch({
    control,
    name: "expenses_list",
  });

  const expensesList: IExpensesList[] = useMemo(
    () => (watchedExpensesList ?? []) as IExpensesList[],
    [watchedExpensesList]
  );

  const filtered = useMemo(() => {
    if (source === "driver1") {
      return expensesList.filter(
        (item) => (item.driver_no ?? DriverNo.Primary) === DriverNo.Primary
      );
    }
    return expensesList.filter((item) => item.driver_no === DriverNo.Secondary);
  }, [expensesList, source]);

  const summary = useMemo(
    () => expensesSummary(filtered),
    [filtered]
  );

  return summary;
};
